'use client';

import { useEffect, useState, useTransition } from 'react';
import { Button } from '../ui/button';
// @ts-ignore
import { useGoogleMaps } from '@jahez-ui/google-maps';
import { ChevronRight, MapPin, X } from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../ui/drawer';
import { useTranslations } from 'next-intl';
import { useLocationStore } from '@/lib/state-stores/locationStore';
import {
  getCookiesLocationDetails,
  setCurrentLoaction,
} from '@/lib/cookies/getCookiesLocationDetails';
import { refetchStores } from '@/lib/actions';
import { AddressTypeStrEnum } from '@/types/address';

type AddressDialogProps = {
  defaultLatitude?: string;
  defaultLongitude?: string;
  lang: 'ar' | 'en';
};

const AddressContent: React.FC<
  AddressDialogProps & { openMap?: boolean; setOpenMap: (inp: boolean) => void }
> = ({ setOpenMap }) => {
  const t = useTranslations('address');
  const { locationDetails, setLocationDetails } = useLocationStore();
  const locationDetailsCookie = getCookiesLocationDetails();
  const [address, setAddress] = useState(t('set-address'));
  const [ispending, setTransition] = useTransition();

  const {
    googleMapComponent,
    currentMarkerAddress,
    currentDistrict,
    currentPostalCode,
    currentCity,
    currentCountry,
    currentRoute,
  } = useGoogleMaps({
    googleApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    lat:
      Number(locationDetails?.location.latitude) ||
      Number(locationDetailsCookie?.location.latitude),
    lng:
      Number(locationDetails?.location.longitude) ||
      Number(locationDetailsCookie?.location.longitude),
    defaults: {
      zoom: 15,
    },

    onCoordsChange(latitude: number, longitude: number) {
      setLocationDetails({
        location: {
          latitude,
          longitude,
        },
        name: currentDistrict || currentPostalCode || locationDetails?.name,
        type: 3,
        details: currentMarkerAddress,
        city: currentCity,
        country: currentCountry,
        typeArabicName: AddressTypeStrEnum.Other,
        typeEnglishName: AddressTypeStrEnum.Other,
        street: currentRoute,
        zip_code: currentPostalCode,
      });
    },
    showPin: true,
    currenLocation: {
      enabled: true,
      onLoadLocation(lat: number, lng: number, event: string) {
        if (!locationDetails?.location.latitude && !locationDetails?.location.longitude) {
          setLocationDetails({
            location: {
              latitude: lat,
              longitude: lng,
            },
            name: currentDistrict || currentPostalCode || locationDetails?.name,
            type: 3,
            details: currentMarkerAddress,
            city: currentCity,
            country: currentCountry,
            typeArabicName: AddressTypeStrEnum.Other,
            typeEnglishName: AddressTypeStrEnum.Other,
            street: currentRoute,
            zip_code: currentPostalCode,
          });
        } else if (event === 'current location') {
          setLocationDetails({
            location: {
              latitude: lat,
              longitude: lng,
            },
            name: currentDistrict || currentPostalCode || locationDetails?.name,
            type: 3,
            details: currentMarkerAddress,
            city: currentCity,
            country: currentCountry,
            typeArabicName: AddressTypeStrEnum.Other,
            typeEnglishName: AddressTypeStrEnum.Other,
            street: currentRoute,
            zip_code: currentPostalCode,
          });
        }
      },
    },
  });

  const handleSaveCoordinates = () => {
    setTransition(() => {
      if (locationDetails?.location.latitude && locationDetails?.location.longitude) {
        const location = {
          latitude: locationDetails.location.latitude,
          longitude: locationDetails.location.longitude,
        };

        setCurrentLoaction({
          details: currentMarkerAddress,
          type: 3,
          name: currentDistrict || locationDetails?.name,
          location: location,
          city: currentCity,
          country: currentCountry,
          typeArabicName: AddressTypeStrEnum.Other,
          typeEnglishName: AddressTypeStrEnum.Other,
          street: currentRoute,
          zip_code: currentPostalCode,
        });
        refetchStores();
        setOpenMap(false);
      }
    });
  };

  useEffect(() => {
    if (locationDetailsCookie?.name) {
      setAddress(locationDetailsCookie?.name);
    } else {
      setAddress(t('set-address'));
    }
  }, [locationDetailsCookie?.name, t]);

  return (
    <>
      <DrawerTrigger asChild>
        <button
          className="group flex flex-row items-center space-x-2 outline-hidden rtl:space-x-reverse"
          onClick={() => setOpenMap(true)}
        >
          <MapPin className="text-primary" size={28} />
          <div className="flex flex-col items-start">
            <div className="text-sm text-neutral-800">{t('address')}</div>
            <div className="text-primary max-w-[100px] truncate text-sm transition-opacity group-hover:opacity-50">
              {address}
            </div>
          </div>
          <ChevronRight className="transform self-end rtl:-scale-x-100" />
        </button>
      </DrawerTrigger>
      <DrawerContent className="bg-white">
        <DrawerHeader>
          <DrawerTitle className="flex items-center justify-between">
            <span>Set location</span>
            <DrawerClose onClick={() => setOpenMap(false)}>
              <X />
            </DrawerClose>
          </DrawerTitle>
        </DrawerHeader>

        <div className="map-parent z-50 mb-4 block h-[60dvh] w-full">{googleMapComponent}</div>

        <DrawerFooter>
          <Button loading={!!ispending} rounded="full" onClick={handleSaveCoordinates}>
            {t('save-address')}
          </Button>

          <DrawerClose asChild>
            <Button rounded="full" variant="outline" onClick={() => setOpenMap(false)}>
              {t('cancel')}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </>
  );
};

export const AddressSheet: React.FC<AddressDialogProps> = (props) => {
  const [openMap, setOpenMap] = useState(false);
  const { setLocationDetails } = useLocationStore();
  const locationDetailsCookie = getCookiesLocationDetails();

  const handleClose = () => {
    setLocationDetails({
      location: {
        latitude: locationDetailsCookie?.location.latitude,
        longitude: locationDetailsCookie?.location.longitude,
      },
      name: locationDetailsCookie?.name,
      type: 3,
      details: locationDetailsCookie?.details,
      city: locationDetailsCookie?.city,
      country: locationDetailsCookie?.country,
      typeArabicName: AddressTypeStrEnum.Other,
      typeEnglishName: AddressTypeStrEnum.Other,
      street: locationDetailsCookie?.street,
    });
    setOpenMap(false);
  };

  return (
    <Drawer open={openMap} onOpenChange={setOpenMap} onClose={handleClose} dismissible={false}>
      <AddressContent setOpenMap={setOpenMap} openMap={openMap} key={`map-${openMap}`} {...props} />
    </Drawer>
  );
};
